import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useContent } from "@/content";
import { Logo } from "@/components/Logo";

export const Nav = ({ onNavigate }) => {
  const { NAV_LINKS, HERO } = useContent();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b transition-[box-shadow,border-color] duration-500 ${scrolled ? "border-slate-200 shadow-md shadow-slate-900/5" : "border-transparent"}`} data-testid="site-header">
      <div className={`max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between transition-[height] duration-500 ${scrolled ? "h-14 md:h-16" : "h-16 md:h-20"}`}>
        <button onClick={() => go("home")} className="flex items-center" data-testid="nav-logo" aria-label="Desantrix home">
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8" data-testid="nav-desktop">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}-link`}
              className="nav-link font-mono text-xs tracking-[0.2em] uppercase text-slate-500 hover:text-amber-600 transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("contact")}
            data-testid="nav-quote-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#0B1528] px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-500 hover:text-[#0B1528] transition-colors duration-300"
          >
            {HERO.primaryCta}
            <ArrowUpRight className="h-4 w-4" />
          </button>
          <button
            className="md:hidden text-slate-700 p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-slate-200 bg-white/95 backdrop-blur-md"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`nav-mobile-${l.id}-link`}
                  className="text-left font-mono text-sm tracking-[0.2em] uppercase text-slate-600 hover:text-amber-600 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("contact")}
                data-testid="nav-mobile-quote-cta"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-[#0B1528] px-5 py-2.5 text-sm font-semibold text-white"
              >
                {HERO.primaryCta}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
